import { useState } from "react";
import useHttp from "./use_http";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import {
  createCourseSchema,
  createLessonSchema,
  createProductSchema,
  createQuizSchema,
  createSectionSchema,
  generateCertificateSchema,
  replyReviewSchema,
  reportReviewSchema,
} from "../utils/schema";
import Env from "../constant/env.json";
import { useLocation, useNavigate } from "react-router-dom";
import { PromotionsEnum } from "../utils/enums";
import moment from "moment";
import { useSelector } from "react-redux";
import showFile from "../helpers/show_file";

const useCourses = () => {
  // hooks
  const navigate = useNavigate();
  const location = useLocation();
  const { httpService } = useHttp();

  // loading states
  const [loadings, setLoadings] = useState({
    getCourses: false,
    getCoursesPaginates: false,
    getCourseById: false,
    createCourse: false,
    updateCourse: false,
    // section
    createSection: false,
    updateSection: false,
    deleteSection: false,
    // lesson
    createLesson: false,
    updateLesson: false,
    deleteLesson: false,
    // quiz
    createQuiz: false,
    updateQuiz: false,
    deleteQuiz: false,
    // product
    createProduct: false,
    updateProduct: false,
    deleteProduct: false,
    // reviews
    getReviews: false,
    getReviewsPaginate: false,
    replyReview: false,
    reportReview: false,
    handleLikeReview: false,
    // members
    getCourseMembers: false,
    generateCertificate: false,
  });
  const [getCategoriesLoading, setGetCategoriesLoading] = useState(false);
  const [getTagsLoading, setGetTagsLoading] = useState(false);
  const [getCollectionsLoading, setGetCollectionsLoading] = useState(false);
  const [uploadContextFileLoading, setUploadContextFileLoading] =
    useState(false);

  // controller states
  const [createCourseFormValidation, setCreateCourseFormValidation] =
    useState(createCourseSchema);
  const [updateCourseFormValidation, setUpdateCourseFormValidation] =
    useState(createCourseSchema);
  const [paginates, setPaginates] = useState({
    current: 1,
    has_more: false,
  });
  const [reviewPaginates, setReviewPaginates] = useState({
    current: 1,
    has_more: false,
  });
  const [membersPaginates, setMembersPaginates] = useState({
    current: 1,
    total: 1,
  });
  const [uploadLessonProgress, setUploadLessonProgress] = useState(0);
  const [certificatePreview, setCertificatePreview] = useState(null);
  const [placedFields, setPlacedFields] = useState([]);

  // modal states
  const [createSectionModal, setCreateSectionModal] = useState(false);
  const [updateSectionModal, setUpdateSectionModal] = useState(false);
  const [deleteSectionModal, setDeleteSectionModal] = useState(false);
  const [createLessonModal, setCreateLessonModal] = useState(false);
  const [updateLessonModal, setUpdateLessonModal] = useState(false);
  const [deleteLessonModal, setDeleteLessonModal] = useState(false);
  const [createQuizModal, setCreateQuizModal] = useState(false);
  const [updateQuizModal, setUpdateQuizModal] = useState(false);
  const [deleteQuizModal, setDeleteQuizModal] = useState(false);
  const [createProductModal, setCreateProductModal] = useState(false);
  const [updateProductModal, setUpdateProductModal] = useState(false);
  const [deleteProductModal, setDeleteProductModal] = useState(false);
  const [replyModal, setReplyModal] = useState(false);
  const [reportModal, setReportModal] = useState(false);
  const [certificateModal, setCertificateModal] = useState(false);
  const [newCertificateModal, setNewCertificateModal] = useState(false);

  // entity states
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [courses, setCourses] = useState([]);
  const [viewCourseData, setViewCourseData] = useState(null);
  const [reviewsData, setReviewsData] = useState([]);
  const [membersData, setMembersData] = useState([]);
  const [collectionsData, setCollectionsData] = useState([]);

  const userProfile = useSelector((state) => state.app.userProfile);

  // methods
  async function apiFileToHtmlFile(apiFile) {
    // Replace with your actual API/file URL
    const fileUrl = `${Env.base_url}file/${apiFile.name}`;

    // Fetch the binary data
    const response = await fetch(fileUrl);
    const blob = await response.blob();

    // Convert blob to File
    const file = new File([blob], apiFile.originalName, {
      type: apiFile.fileType,
      lastModified: new Date(apiFile.updateTime).getTime(),
    });

    return file;
  }

  const getCategories = async () => {
    let array = [];
    try {
      setGetCategoriesLoading(true);
      const response = await httpService.get("category");
      setGetCategoriesLoading(false);
      response.data.map((item) => {
        array.push({
          label: item.translations?.en?.name,
          value: item.id,
        });
      });
      setCategories(array);
    } catch ({ err, response }) {
      setGetCategoriesLoading(false);
    }
  };

  const getTags = async (category_id, languege_id) => {
    let paramsData = {
      page: 1,
      limit: 1000,
      categoryId: category_id,
      lang: languege_id,
    };
    try {
      setGetTagsLoading(true);
      const response = await httpService.get("tag", {
        params: paramsData,
      });
      setGetTagsLoading(false);
      setTags(response.data.data);
    } catch ({ err, response }) {
      setGetTagsLoading(false);
    }
  };

  const getCourses = async (page) => {
    let array = [...courses];
    let paramsData = {
      page: page,
      limit: 10,
    };
    try {
      if (page == 1) {
        setLoadings({ ...loadings, getCourses: true });
      } else {
        setLoadings({ ...loadings, getCoursesPaginates: true });
      }
      const response = await httpService.get("instructor/course/list", {
        params: paramsData,
      });
      setLoadings({
        ...loadings,
        getCourses: false,
        getCoursesPaginates: false,
      });
      setPaginates({
        ...paginates,
        current: page,
        has_more: response.data.pageCount > page ? true : false,
      });
      if (response.data.courses) {
        if (page > 1) {
          response.data.courses.map((item) => {
            array.push(item);
          });
          setCourses(array);
        } else {
          setCourses(response.data.courses);
        }
      } else {
        setCourses([]);
      }
    } catch ({ err, response }) {
      setLoadings({
        ...loadings,
        getCourses: false,
        getCoursesPaginates: false,
      });
    }
  };

  const getCourseById = async (course_id) => {
    let tagsArray = [];
    try {
      setLoadings({ ...loadings, getCourseById: true });
      const response = await httpService.get(`course/${course_id}`);
      if (location.pathname.includes("/courses/edit")) {
        let course = response.data.course;
        updateCourseController.setFieldValue("name", course.name);
        updateCourseController.setFieldValue("level", course.level);
        updateCourseController.setFieldValue("description", course.description);
        updateCourseController.setFieldValue("prices", course.prices);
        updateCourseController.setFieldValue("categoryId", course.category?.id);
        updateCourseController.setFieldValue("languegeId", course.language);
        if (course.certificate) {
          updateCourseController.setFieldValue("has_certificate", true);
          updateCourseController.setFieldValue("certificate", {
            id: course?.certificate?.id,
            file: course?.certificate,
            url: showFile(course?.certificate?.name),
          });
        }
        if (course.discounts?.length > 0) {
          updateCourseController.setFieldValue("has_promotion", true);
          updateCourseController.setFieldValue("promotion", {
            type: PromotionsEnum[0].value,
            amount: course.discounts[0]?.amount,
          });
        }
        course.tags?.map((tag) => {
          tagsArray.push(tag.tag.id);
        });
        updateCourseController.setFieldValue("tagIds", tagsArray);
        updateCourseController.setFieldValue("free", course.free);
        if (course.banner) {
          updateCourseController.setFieldValue("banner", {
            id: course?.banner?.id,
            file: course?.banner,
            url: showFile(course?.banner?.name),
          });
        }
        if (course.preview) {
          updateCourseController.setFieldValue("coursePreview", {
            id: course?.preview?.id,
            file: course?.preview,
            url: showFile(course?.preview?.name),
          });
        }
      }
      setLoadings({ ...loadings, getCourseById: false });
      const viewData = response.data;
      if (viewData?.course?.sections) {
        viewData.course.sections = viewData.course.sections
          .sort((a, b) => a.order - b.order)
          .map((section) => ({
            ...section,
            lessons: section.lessons
              ? section.lessons.sort((a, b) => a.order - b.order)
              : [],
          }));
      }
      setViewCourseData(viewData);
    } catch ({ err, response }) {
      setLoadings({ ...loadings, getCourseById: false });
    }
  };

  const createCourseController = useFormik({
    initialValues: {
      name: "",
      level: null,
      description: "",
      prices: [
        {
          price: "",
          currency: null,
        },
      ],
      categoryId: null,
      languegeId: null,
      has_certificate: false,
      certificate: null,
      certificatePositions: {
        name: {
          x: null,
          y: null,
          width: 30,
          height: 7,
          fontSize: 48,
          color: "#000000",
          align: "center",
          anchor: "center",
        },
        date: {
          x: null,
          y: null,
          width: 22,
          height: 6,
          fontSize: 48,
          color: "#000000",
          align: "center",
          anchor: "center",
        },
      },
      has_promotion: null,
      promotion: null,
      tagIds: [],
      free: false,
      banner: null,
      coursePreview: null,
    },
    validationSchema: createCourseFormValidation,
    enableReinitialize: true,
    onSubmit: (values) => {
      createCourse(values);
    },
  });

  const createCourse = async (values) => {
    let postData = new FormData();
    postData.append("type", 1);
    postData.append("name", values.name);
    postData.append("level", values.level);
    postData.append("description", values.description);
    postData.append(
      "prices",
      JSON.stringify(
        values.prices.map((item) => {
          return {
            currency: item.currency,
            price: parseFloat(item.price),
          };
        }),
      ),
    );
    postData.append("categoryId", parseInt(values.categoryId));
    postData.append("languegeId", values.languegeId);
    if (values.certificate) {
      postData.append("certificate", values.certificate);
      postData.append(
        "certificatePositions",
        JSON.stringify(values.certificatePositions),
      );
    }
    if (values.has_promotion && values.promotion) {
      postData.append(
        "promotion",
        JSON.stringify({
          type: values.promotion?.type,
          amount: parseFloat(values.promotion?.amount),
        }),
      );
    }
    postData.append("tagIds", JSON.stringify(values.tagIds));
    postData.append("free", values.free);
    if (values.banner) {
      postData.append("banner", values.banner);
    }
    if (values.coursePreview) {
      postData.append("coursePreview", values.coursePreview);
    }
    try {
      setLoadings({ ...loadings, createCourse: true });
      const response = await httpService.post("course", postData, {
        onUploadProgress: (e) => {
          if (e.total) {
            const percent = Math.round((e.loaded * 100) / e.total);
            setUploadLessonProgress(percent);
          }
        },
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLoadings({ ...loadings, createCourse: false });
      if (response.status < 400) {
        toast.success(response.data.message);
        navigate(`/courses/view/${response.data.course?.id}`);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, createCourse: false });
    }
  };

  const updateCourseController = useFormik({
    initialValues: {
      name: "",
      level: null,
      description: "",
      prices: [
        {
          price: "",
          currency: null,
        },
      ],
      categoryId: null,
      languegeId: null,
      has_certificate: false,
      certificate: null,
      certificatePositions: {
        name: {
          x: null,
          y: null,
          width: 30,
          height: 7,
          fontSize: 48,
          color: "#000000",
          align: "center",
          anchor: "center",
        },
        date: {
          x: null,
          y: null,
          width: 22,
          height: 6,
          fontSize: 48,
          color: "#000000",
          align: "center",
          anchor: "center",
        },
      },
      has_promotion: null,
      promotion: null,
      tagIds: [],
      free: false,
      banner: null,
      coursePreview: null,
    },
    validationSchema: updateCourseFormValidation,
    enableReinitialize: true,
    onSubmit: (values) => {
      updateCourse(values);
    },
  });

  const updateCourse = async (values) => {
    let postData = new FormData();
    postData.append("type", 1);
    postData.append("name", values.name);
    postData.append("level", values.level);
    postData.append("description", values.description);
    postData.append(
      "prices",
      JSON.stringify(
        values.prices.map((item) => {
          return {
            currency: item.currency,
            price: parseFloat(item.price),
          };
        }),
      ),
    );
    postData.append("categoryId", parseInt(values.categoryId));
    postData.append("languegeId", values.languegeId);
    if (values.certificate && !values.certificate.id) {
      postData.append("certificate", values.certificate?.file);
      postData.append(
        "certificatePositions",
        JSON.stringify(values.certificatePositions),
      );
    } else if (!values.certificate) {
      postData.append("certificate", null);
    }
    if (values.has_promotion) {
      postData.append(
        "promotion",
        JSON.stringify({
          type: values.promotion?.type,
          amount: parseFloat(values.promotion?.amount),
        }),
      );
    } else {
      postData.append("promotion", null);
    }
    postData.append("tagIds", JSON.stringify(values.tagIds));
    postData.append("free", values.free);
    if (values.banner && !values.banner?.id) {
      postData.append("banner", values.banner?.file);
    } else if (!values.banner) {
      postData.append("banner", null);
    }
    if (values.coursePreview && !values.coursePreview?.id) {
      postData.append("coursePreview", values.coursePreview?.file);
    } else if (!values.coursePreview) {
      postData.append("coursePreview", null);
    }
    try {
      setLoadings({ ...loadings, updateCourse: true });
      const response = await httpService.put(
        `course/${location.pathname.split("/")[3]}`,
        postData,
        {
          onUploadProgress: (e) => {
            if (e.total) {
              const percent = Math.round((e.loaded * 100) / e.total);
              setUploadLessonProgress(percent);
            }
          },
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      setLoadings({ ...loadings, updateCourse: false });
      if (response.status < 400) {
        toast.success(response.data.message);
        navigate("/courses");
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, updateCourse: false });
    }
  };

  // section ------------------------------------------------
  const createSectionController = useFormik({
    initialValues: {
      title: "",
      description: "",
    },
    validationSchema: createSectionSchema,
    onSubmit: (values) => {
      createSection(values);
    },
  });

  const createSection = async (values) => {
    let postData = {
      sections: [
        {
          title: values.title,
          description: values.description,
        },
      ],
    };
    try {
      setLoadings({ ...loadings, createSection: true });
      const response = await httpService.post(
        `section/${location.pathname.split("/")[3]}`,
        postData,
      );
      setLoadings({ ...loadings, createSection: false });
      if (response.status < 400) {
        toast.success(response.data.message);
        setCreateSectionModal(false);
        getCourseById(location.pathname.split("/")[3]);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, createSection: false });
    }
  };

  const updateSectionController = useFormik({
    initialValues: {
      id: null,
      title: "",
      description: "",
    },
    validationSchema: createSectionSchema,
    onSubmit: (values) => {
      updateSection(values);
    },
  });

  const updateSection = async (values) => {
    let postData = {
      title: values.title,
      description: values.description,
    };
    try {
      setLoadings({ ...loadings, updateSection: true });
      const response = await httpService.put(`section/${values.id}`, postData);
      setLoadings({ ...loadings, updateSection: false });
      if (response.status < 400) {
        toast.success(response.data.message);
        setUpdateSectionModal(false);
        getCourseById(location.pathname.split("/")[3]);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, updateSection: false });
    }
  };

  const deleteSection = async (section_id) => {
    try {
      setLoadings({ ...loadings, deleteSection: true });
      const response = await httpService.delete(`section/${section_id}`);
      setLoadings({ ...loadings, deleteSection: false });
      if (response.status < 400) {
        toast.success(response.data.message);
        setDeleteSectionModal(false);
        getCourseById(location.pathname.split("/")[3]);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, deleteSection: false });
    }
  };

  const reorderSections = async (ids) => {
    try {
      const response = await httpService.put(`section/sort`, {
        ids: ids,
      });
    } catch ({ err, response }) {}
  };

  // lesson ------------------------------------------------
  const createLessonController = useFormik({
    initialValues: {
      section_id: null,
      title: "",
      description: "",
      free: false,
      video: null,
      videoId: null,
      attachment: [],
      context: "",
    },
    validationSchema: createLessonSchema,
    onSubmit: (values) => {
      createLesson(values);
    },
  });

  const createLesson = async (values) => {
    let postData = new FormData();
    postData.append("title", values.title);
    if (values.description?.length > 0) {
      postData.append("description", values.description);
    }
    postData.append("free", values.free);
    if (values.video) {
      postData.append("video", values.video?.file);
    }
    if (values?.videoId) {
      postData.append("videoId", values.videoId?.guid);
    }
    if (values.attachment?.length > 0) {
      values.attachment?.map((file) => {
        postData.append("attachment", file);
      });
    }
    if (values.context?.length > 0) {
      postData.append("context", values.context);
    }
    try {
      setLoadings({ ...loadings, createLesson: true });
      const response = await httpService.post(
        `section/lesson/${values.section_id}`,
        postData,
        {
          onUploadProgress: (e) => {
            if (e.total) {
              const percent = Math.round((e.loaded * 100) / e.total);
              setUploadLessonProgress(percent);
            }
          },
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      setLoadings({ ...loadings, createLesson: false });
      if (response.status < 400) {
        toast.success(response.data.message);
        setCreateLessonModal(false);
        getCourseById(location.pathname.split("/")[3]);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, createLesson: false });
    }
  };

  const updateLessonController = useFormik({
    initialValues: {
      id: null,
      title: "",
      description: "",
      free: false,
      video: null,
      videoId: null,
      attachment: [],
      context: "",
    },
    validationSchema: createLessonSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      updateLesson(values);
    },
  });

  const updateLesson = async (values) => {
    let postData = new FormData();
    postData.append("title", values.title);
    if (values.description?.length > 0) {
      postData.append("description", values.description);
    }
    postData.append("free", values.free);
    if (values.video && !values.video?.id) {
      postData.append("video", values.video?.file);
    }
    if (values.videoId) {
      postData.append("videoId", values.videoId?.guid);
    }
    if (values.attachment?.length > 0) {
      values.attachment?.map((file) => {
        postData.append("attachment", file);
      });
    } else {
      postData.append("attachment", null);
    }
    if (values.context?.length > 0) {
      postData.append("context", values.context);
    }
    try {
      setLoadings({ ...loadings, updateLesson: true });
      const response = await httpService.put(
        `section/lesson/${values.id}`,
        postData,
        {
          onUploadProgress: (e) => {
            if (e.total) {
              const percent = Math.round((e.loaded * 100) / e.total);
              setUploadLessonProgress(percent);
            }
          },
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      setLoadings({ ...loadings, updateLesson: false });
      if (response.status < 400) {
        toast.success(response.data.message);
        setUpdateLessonModal(false);
        getCourseById(location.pathname.split("/")[3]);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, updateLesson: false });
    }
  };

  const deleteLesson = async (lesson_id) => {
    try {
      setLoadings({ ...loadings, deleteLesson: true });
      const response = await httpService.delete(`section/lesson/${lesson_id}`);
      setLoadings({ ...loadings, deleteLesson: false });
      if (response.status < 400) {
        toast.success(response.data.message);
        setDeleteLessonModal(false);
        getCourseById(location.pathname.split("/")[3]);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, deleteLesson: false });
    }
  };

  const reorderLessons = async (ids) => {
    try {
      const response = await httpService.put(`section/lesson/sort`, {
        ids: ids,
      });
    } catch ({ err, response }) {}
  };

  const getCollections = async () => {
    let paramsData = {
      page: 1,
      limit: 10000,
      status: 1,
    };
    try {
      setGetCollectionsLoading(true);
      const response = await httpService.get("instructor/course/list", {
        params: paramsData,
      });
      setGetCollectionsLoading(false);
      if (response.data.courses) {
        setCollectionsData(
          response.data.courses?.map((item) => {
            return { ...item, loading: false, videos: [] };
          }),
        );
      } else {
        setCollectionsData([]);
      }
    } catch ({ err, response }) {
      setGetCollectionsLoading(false);
    }
  };

  const getCollectionVideos = async (collection_id, course_id) => {
    try {
      setCollectionsData(
        collectionsData.map((item) => {
          if (item.id == course_id) {
            return { ...item, loading: true };
          } else {
            return item;
          }
        }),
      );
      const response = await httpService.get(`bunny-net/videos`, {
        params: {
          collectionId: collection_id,
        },
      });
      setCollectionsData(
        collectionsData.map((item) => {
          if (item.id == course_id) {
            return { ...item, loading: false };
          } else {
            return item;
          }
        }),
      );
      setCollectionsData(
        collectionsData.map((item) => {
          if (item.id == course_id) {
            return { ...item, videos: response.data.videos };
          } else {
            return item;
          }
        }),
      );
    } catch ({ err, response }) {
      setCollectionsData(
        collectionsData.map((item) => {
          if (item.id == course_id) {
            return { ...item, loading: false };
          } else {
            return item;
          }
        }),
      );
    }
  };

  const uploadContextFile = async (file) => {
    const postData = new FormData();

    postData.append("avatar", file);

    try {
      setUploadContextFileLoading(true);

      const response = await httpService.post("file/upload", postData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setUploadContextFileLoading(false);

      const uploadedFile = response.data;

      if (!uploadedFile?.name) {
        throw new Error("Upload response does not include file name");
      }

      return {
        ...uploadedFile,
        url: showFile(uploadedFile.name),
      };
    } catch (error) {
      setUploadContextFileLoading(false);
      toast.error("Image upload failed");
      throw error;
    }
  };

  // quiz ------------------------------------------------
  const createQuizController = useFormik({
    initialValues: {
      questions: [
        {
          title: "",
          answers: [
            {
              title: "",
              isAnswer: false,
            },
          ],
        },
      ],
      quiz_title: "",
      section_id: null,
    },
    validationSchema: createQuizSchema,
    onSubmit: (values) => {
      createQuiz(values);
    },
  });

  const createQuiz = async (values) => {
    let postData = {
      sectionId: values.section_id,
      quizzes: [
        {
          title: values.quiz_title,
          questions: [],
        },
      ],
    };
    values.questions?.map((ques) => {
      postData.quizzes[0].questions.push({
        title: ques.title,
        options: ques.answers,
      });
    });
    try {
      setLoadings({ ...loadings, createQuiz: true });
      const response = await httpService.post("quizz", postData);
      setLoadings({ ...loadings, createQuiz: false });
      if (response.status < 400) {
        toast.success(response.data.message);
        setCreateQuizModal(false);
        getCourseById(location.pathname.split("/")[3]);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, createQuiz: false });
    }
  };

  const updateQuizController = useFormik({
    initialValues: {
      questions: [
        {
          id: null,
          title: "",
          answers: [
            {
              id: null,
              title: "",
              isAnswer: false,
            },
          ],
        },
      ],
      quiz_title: "",
      quiz_id: null,
    },
    validationSchema: createQuizSchema,
    onSubmit: (values) => {
      updateQuiz(values);
    },
  });

  const updateQuiz = async (values) => {
    let postData = {
      title: values.quiz_title,
      questions: [],
    };
    values.questions?.map((ques) => {
      postData.questions.push({
        id: ques.id,
        title: ques.title,
        options: ques.answers,
      });
    });
    try {
      setLoadings({ ...loadings, updateQuiz: true });
      const response = await httpService.put(
        `quizz/${values.quiz_id}`,
        postData,
      );
      setLoadings({ ...loadings, updateQuiz: false });
      if (response.status < 400) {
        toast.success(response.data.message);
        setUpdateQuizModal(false);
        getCourseById(location.pathname.split("/")[3]);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, updateQuiz: false });
    }
  };

  const deleteQuiz = async (quiz_id) => {
    try {
      setLoadings({ ...loadings, deleteQuiz: true });
      const response = await httpService.delete(`quizz/${quiz_id}`);
      setLoadings({ ...loadings, deleteQuiz: false });
      if (response.status < 400) {
        toast.success(response.data.message);
        setDeleteQuizModal(false);
        getCourseById(location.pathname.split("/")[3]);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, deleteQuiz: false });
    }
  };

  // product ------------------------------------------------
  const createProductController = useFormik({
    initialValues: {
      title: "",
      description: "",
      link: "",
      lessonIds: [],
      product: null,
    },
    validationSchema: createProductSchema,
    onSubmit: (values) => {
      createProduct(values);
    },
  });

  const createProduct = async (values) => {
    let postData = new FormData();
    postData.append("title", values.title);
    postData.append("description", values.description);
    postData.append("link", values.link);
    postData.append("lessonIds", JSON.stringify(values.lessonIds));
    if (values.product) {
      postData.append("product", values.product?.file);
    }
    try {
      setLoadings({ ...loadings, createProduct: true });
      const response = await httpService.post("product", postData);
      setLoadings({ ...loadings, createProduct: false });
      if (response.status < 400) {
        toast.success(response.data.message);
        setCreateProductModal(false);
        getCourseById(location.pathname.split("/")[3]);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, createProduct: false });
    }
  };

  const updateProductController = useFormik({
    initialValues: {
      id: null,
      title: "",
      description: "",
      link: "",
      lessonIds: [],
      product: null,
    },
    validationSchema: createProductSchema,
    onSubmit: (values) => {
      updateProduct(values);
    },
  });

  const updateProduct = async (values) => {
    let postData = new FormData();
    postData.append("title", values.title);
    postData.append("description", values.description);
    postData.append("link", values.link);
    postData.append("lessonIds", JSON.stringify(values.lessonIds));
    if (values.product && !values.product?.id) {
      postData.append("product", values.product?.file);
    } else if (!values.product) {
      postData.append("product", null);
    }

    try {
      setLoadings({ ...loadings, updateProduct: true });
      const response = await httpService.put(`product/${values.id}`, postData);
      setLoadings({ ...loadings, updateProduct: false });
      if (response.status < 400) {
        toast.success(response.data.message);
        setUpdateProductModal(false);
        getCourseById(location.pathname.split("/")[3]);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, updateProduct: false });
    }
  };

  const deleteProduct = async (product_id) => {
    try {
      setLoadings({ ...loadings, deleteProduct: true });
      const response = await httpService.delete(`product/${product_id}`);
      setLoadings({ ...loadings, deleteProduct: false });
      if (response.status < 400) {
        toast.success(response.data.message);
        setDeleteProductModal(false);
        getCourseById(location.pathname.split("/")[3]);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, deleteProduct: false });
    }
  };

  // reviews ------------------------------------------------
  const getReviews = async (page) => {
    let array = [...reviewsData];
    let paramsData = {
      page: page,
      limit: 10,
      courseId: location.pathname.split("/")[3],
    };
    try {
      if (page == 1) {
        setLoadings({ ...loadings, getReviews: true });
      } else {
        setLoadings({ ...loadings, getReviewsPaginate: true });
      }
      const response = await httpService.get(`instructor/dashboard/review`, {
        params: paramsData,
      });
      setLoadings({
        ...loadings,
        getReviews: false,
        getReviewsPaginate: false,
      });
      setReviewPaginates({
        ...reviewPaginates,
        current: page,
        has_more: response.data.pageCount > page ? true : false,
      });
      if (page > 1) {
        response.data.data.map((item) => {
          array.push(item);
        });
        setReviewsData(array);
      } else {
        setReviewsData(response.data.data);
      }
    } catch ({ err, response }) {
      setLoadings({
        ...loadings,
        getReviews: false,
        getReviewsPaginate: false,
      });
    }
  };

  const replyReviewController = useFormik({
    initialValues: {
      reviewId: null,
      comment: "",
    },
    validationSchema: replyReviewSchema,
    onSubmit: (values) => {
      replyReview(values);
    },
  });

  const replyReview = async (values) => {
    try {
      setLoadings({ ...loadings, replyReview: true });
      const response = await httpService.post(`review/reply`, {
        reviewId: values.reviewId,
        comment: values.comment,
      });
      setLoadings({ ...loadings, replyReview: false });
      if (response.status < 400) {
        toast.success(response.data.message);
        setReplyModal(false);
        let array = [...reviewsData];
        let selectedReview = array.find((o) => o.id == values.reviewId);
        let selectedReviewIndex = array.findIndex(
          (o) => o.id == values.reviewId,
        );
        selectedReview.replies.push({
          comment: values.comment,
          rating: null,
          createTime: `${moment().format("YYYY-MM-DD")}T${moment().format(
            "HH:mm:ss",
          )}Z`,
          user: {
            id: userProfile?.id,
            name: userProfile?.fullname,
            email: userProfile?.email,
            avatar: userProfile?.avatar ? userProfile?.avatar : null,
          },
        });
        array[selectedReviewIndex] = selectedReview;
        setReviewsData(array);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, replyReview: false });
    }
  };

  const reportReviewController = useFormik({
    initialValues: {
      reviewId: null,
      reason: "",
    },
    validationSchema: reportReviewSchema,
    onSubmit: (values) => {
      reportReview(values);
    },
  });

  const reportReview = async (values) => {
    try {
      setLoadings({ ...loadings, reportReview: true });
      const response = await httpService.post(`review/report`, {
        reviewId: values.reviewId,
        reason: values.reason,
      });
      setLoadings({ ...loadings, reportReview: false });
      if (response.status < 400) {
        toast.success(response.data.message);
        setReportModal(false);
        let array = [...reviewsData];
        let selectedReview = array.find((o) => o.id == values.reviewId);
        let selectedReviewIndex = array.findIndex(
          (o) => o.id == values.reviewId,
        );
        selectedReview.report = {
          createTime: moment().format("YYYY/MM/DD HH:mm:ss"),
        };
        array[selectedReviewIndex] = selectedReview;
        setReviewsData(array);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, replyReview: false });
    }
  };

  const handleLikeReview = async (review, position) => {
    let likeValue = null;

    if (position === "like") {
      likeValue = review.isLiked === true ? null : true;
    } else {
      likeValue = review.isLiked === false ? null : false;
    }

    const computeCounts = (review, likeValue) => {
      let likeCount = review.likeCount || 0;
      let dislikeCount = review.dislikeCount || 0;

      if (position === "like") {
        if (review.isLiked === true) {
          likeCount--;
        } else if (review?.isLiked == false) {
          likeCount++;
          dislikeCount--;
        } else {
          likeCount++;
        }
      } else {
        if (review.isLiked === false) {
          dislikeCount--;
        } else if (review?.isLiked === true) {
          dislikeCount++;
          likeCount--;
        } else {
          dislikeCount++;
        }
      }

      return { likeCount, dislikeCount };
    };

    const { likeCount, dislikeCount } = computeCounts(review, likeValue);

    try {
      setLoadings({ ...loadings, handleLikeReview: true });
      await httpService.get(`review/like/${review.id}?like=${likeValue}`);
      setLoadings({ ...loadings, handleLikeReview: false });
      setReviewsData((prev) =>
        prev.map((r) =>
          r.id === review.id
            ? { ...r, isLiked: likeValue, likeCount, dislikeCount }
            : r,
        ),
      );
    } catch (error) {
      setLoadings({ ...loadings, handleLikeReview: false });
    }
  };

  // members ------------------------------------------------
  const getCourseMembers = async (page) => {
    let paramsData = {
      page: page,
      limit: 10,
      courseId: location.pathname.split("/")[3],
    };
    try {
      setLoadings({ ...loadings, getCourseMembers: true });
      const response = await httpService.get(`instructor/dashboard/members`, {
        params: paramsData,
      });
      setLoadings({ ...loadings, getCourseMembers: false });
      setMembersPaginates({
        ...membersPaginates,
        current: response.data.page,
        total: response.data.pageCount,
      });
      setMembersData(response.data.data);
    } catch ({ err, response }) {
      setLoadings({ ...loadings, getCourseMembers: false });
    }
  };

  const generateCertificateController = useFormik({
    initialValues: {
      userId: null,
      certificate: null,
    },
    validationSchema: generateCertificateSchema,
    onSubmit: (values) => {
      generateCertificate(values);
    },
  });

  const generateCertificate = async (values) => {
    let postData = new FormData();
    postData.append("certificate", values.certificate?.file);
    try {
      setLoadings({ ...loadings, generateCertificate: true });
      const response = await httpService.post(
        `certificate/upload/${values.userId}/${
          location.pathname.split("/")[3]
        }`,
        postData,
      );
      setLoadings({ ...loadings, generateCertificate: false });
      if (response.status < 400) {
        toast.success(response.data.message);
        setCertificateModal(false);
        getCourseMembers(paginates.current);
      }
    } catch ({ err, response }) {
      setLoadings({ ...loadings, generateCertificate: false });
    }
  };

  const exports = {
    // entity methods
    apiFileToHtmlFile,
    getCategories,
    getTags,
    getCourses,
    getCourseById,
    deleteSection,
    deleteLesson,
    deleteQuiz,
    deleteProduct,
    getReviews,
    getCourseMembers,
    reorderSections,
    reorderLessons,
    handleLikeReview,
    getCollections,
    getCollectionVideos,
    uploadContextFile,
    // controller methods
    createCourseController,
    updateCourseController,
    createSectionController,
    updateSectionController,
    createLessonController,
    updateLessonController,
    createQuizController,
    updateQuizController,
    createProductController,
    updateProductController,
    replyReviewController,
    reportReviewController,
    generateCertificateController,
    // entity states
    categories,
    tags,
    courses,
    viewCourseData,
    reviewsData,
    membersData,
    collectionsData,
    setCollectionsData,
    // controller states
    setCreateCourseFormValidation,
    setUpdateCourseFormValidation,
    paginates,
    reviewPaginates,
    membersPaginates,
    uploadLessonProgress,
    setUploadLessonProgress,
    certificatePreview,
    setCertificatePreview,
    placedFields,
    setPlacedFields,
    // modal states
    createSectionModal,
    setCreateSectionModal,
    updateSectionModal,
    setUpdateSectionModal,
    deleteSectionModal,
    setDeleteSectionModal,
    createLessonModal,
    setCreateLessonModal,
    updateLessonModal,
    setUpdateLessonModal,
    deleteLessonModal,
    setDeleteLessonModal,
    createQuizModal,
    setCreateQuizModal,
    updateQuizModal,
    setUpdateQuizModal,
    deleteQuizModal,
    setDeleteQuizModal,
    createProductModal,
    setCreateProductModal,
    updateProductModal,
    setUpdateProductModal,
    deleteProductModal,
    setDeleteProductModal,
    replyModal,
    setReplyModal,
    reportModal,
    setReportModal,
    certificateModal,
    setCertificateModal,
    newCertificateModal,
    setNewCertificateModal,
    // loading states
    loadings,
    getCategoriesLoading,
    getTagsLoading,
    getCollectionsLoading,
    uploadContextFileLoading,
  };
  return exports;
};
export default useCourses;
